import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { HttpClient, HttpBackend, HttpHeaders } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import { firstValueFrom, BehaviorSubject } from 'rxjs';

export type VersionPayload = {
    buildId?: string;
    shortSha?: string;
    builtAt?: string;
    buildNumber?: number;
    displayVersion?: string;
};

@Injectable({ providedIn: 'root' })
export class VersionService {
    private currentId?: string;
    private timer?: any;
    private rawHttp: HttpClient;
    private readonly LOCK_KEY = '__buki_reload_lock__';

    public info$ = new BehaviorSubject<VersionPayload | null>(null);

    constructor(
        handler: HttpBackend,
        @Inject(PLATFORM_ID) private platformId: Object
    ) {
        this.rawHttp = new HttpClient(handler);
    }

    async init(pollMs = 120000) {
        if (!isPlatformBrowser(this.platformId)) return;

        // limpia el candado en cada arranque
        sessionStorage.removeItem(this.LOCK_KEY);

        // primer fetch (sin bust, pero con ngsw-bypass)
        const first = await this.fetch().catch(() => undefined);
        if (first?.buildId) this.currentId = first.buildId;
        if (first) this.info$.next(first);

        this.startPolling(pollMs);

        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') this.startPolling(pollMs);
            else this.stop();
        });

        window.addEventListener('online', () => this.startPolling(pollMs));
        window.addEventListener('offline', () => this.stop());
    }

    stop() {
        if (this.timer) clearInterval(this.timer);
        this.timer = undefined;
    }

    public async checkNow() {
        const v = await this.fetch();
        this.applyIfChanged(v);
    }

    // Reemplaza tu fetch(...) por uno único:
    private async fetch(): Promise<VersionPayload | undefined> {
        const url = `/assets/version.json?ngsw-bypass=true&_=${Date.now()}`;
        try {
            return await firstValueFrom(
                this.rawHttp.get<VersionPayload>(url, {
                    responseType: 'json' as any,
                    headers: new HttpHeaders({ 'x-no-loader': 'true' }), // sin token/loader
                })
            );
        } catch {
            return undefined;
        }
    }


    private startPolling(ms: number) {
        if (this.timer) return;
        this.timer = setInterval(async () => {
            try {
                if (document.visibilityState !== 'visible') return;
                if (!navigator.onLine) return;

                const next = await this.fetch();
                this.applyIfChanged(next);
            } catch {
                // silencioso
            }
        }, ms);
    }

    private applyIfChanged(next?: VersionPayload): void {
        if (!next) return;

        this.info$.next(next);

        const nextId = next.buildId || next.shortSha || '';
        if (!this.currentId) {
            if (nextId) this.currentId = nextId;
            return;
        }

        if (sessionStorage.getItem(this.LOCK_KEY) === '1') return;

        if (nextId && nextId !== this.currentId) {
            const MIN_AGE_MS = 0;
            const builtAtMs = next.builtAt ? Date.parse(next.builtAt) : undefined;
            const isOldEnough = builtAtMs ? (Date.now() - builtAtMs) >= MIN_AGE_MS : true;

            if (isOldEnough) {
                sessionStorage.setItem(this.LOCK_KEY, '1');
                // for (const k of ['userCache','cart','filters']) localStorage.removeItem(k);
                location.reload();
                return;
            }
        }

        if (nextId) this.currentId = nextId;
    }

    public formatLabel(v: VersionPayload | null): string {
        if (!v) return '';
        const sha = v.shortSha || (v.buildId || '').slice(0, 7);
        return v.displayVersion ? `${v.displayVersion} (${sha})` : `${sha}`;
    }
}
